import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { faCheck, faEye, faXmark } from '@fortawesome/pro-duotone-svg-icons'

import Icon from '../icon/Icon'
import ToggleSwitch from '../toggle-switch/ToggleSwitch'
import { useAuth } from '../../_hooks/use-auth'
import { defaultQuickPassSharePageVisibility } from '../../_defaults/quickpass-share-page'
import {
  checkShareSlugAvailability,
  getQuickPassSharePage,
  regenerateQuickPassShareToken,
  saveQuickPassSharePage,
} from '../../_data/share-page'
import { QuickPassSharePageVisibility } from '../../_types/quickpass-share-page'
import { ShareManagerState, SlugCheckStatus } from './types'
import { buildShareLink, formatLastViewed, VISIBILITY_LABELS } from './helpers'
import {
  Banner,
  DangerLink,
  FieldLabel,
  FieldStack,
  InactiveBanner,
  Lede,
  ManagerRoot,
  PrimaryButton,
  SecondaryButton,
  ShareLinkActions,
  ShareLinkBox,
  ShareLinkRow,
  SlugHint,
  SlugInput,
  SlugInputRow,
  SlugPrefix,
  SlugRow,
  StatsRow,
  TextArea,
  TextField,
  TopRow,
  VisibilityDescription,
  VisibilityList,
  VisibilityName,
  VisibilityRow,
  VisibilityText,
} from './styled'

const SLUG_DEBOUNCE_MS = 350

const slugSuggestionFromName = (
  firstName: string,
  lastName: string,
): string => {
  const combined = `${firstName}-${lastName}`.toLowerCase()
  const cleaned = combined.replace(/[^a-z0-9-]/gu, '').replace(/-+/gu, '-')
  return cleaned.replace(/^-+|-+$/gu, '')
}

const initialiseState = (): ShareManagerState => ({
  page: null,
  slugDraft: '',
  headlineDraft: '',
  customMessageDraft: '',
  visibilityDraft: { ...defaultQuickPassSharePageVisibility },
  isActiveDraft: true,
  slugCheckStatus: 'idle',
  slugCheckResult: null,
  isLoading: true,
  isSaving: false,
  isRegenerating: false,
  hasPendingChanges: false,
  errorMessage: '',
  successMessage: '',
})

/**
 * "Share your QuickPass" manager — lives on the profile page. Lets the
 * renter pick a username-style slug (with live availability check),
 * write a headline + note, toggle which sections appear on the public
 * page (LinkedIn-style), copy the share link, and revoke + regenerate
 * the token. Shows view-count stats once a page exists.
 *
 * @returns {JSX.Element | null} null while auth is resolving
 */
const SharePageManager = (): JSX.Element | null => {
  const { quickPassUser } = useAuth()
  const [state, setState] = useState<ShareManagerState>(initialiseState)
  const slugDebounceReference = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )

  useEffect(() => {
    if (quickPassUser === null) {
      setState(previous => ({ ...previous, isLoading: false }))
      return
    }

    let isMounted = true

    const load = async (): Promise<void> => {
      const page = await getQuickPassSharePage(quickPassUser.id)
      if (isMounted === false) return

      if (page === null) {
        const suggested = slugSuggestionFromName(
          quickPassUser.displayName?.split(' ')[0] ?? '',
          quickPassUser.displayName?.split(' ')[1] ?? '',
        )
        setState(previous => ({
          ...previous,
          page: null,
          slugDraft: suggested,
          isLoading: false,
        }))
        return
      }

      setState(previous => ({
        ...previous,
        page,
        slugDraft: page.slug,
        headlineDraft: page.headline,
        customMessageDraft: page.customMessage,
        visibilityDraft: {
          ...defaultQuickPassSharePageVisibility,
          ...page.visibility,
        },
        isActiveDraft: page.isActive,
        isLoading: false,
      }))
    }

    void load()

    return () => {
      isMounted = false
    }
  }, [quickPassUser])

  const runSlugCheck = useCallback(
    (candidate: string): void => {
      if (quickPassUser === null) return
      if (slugDebounceReference.current !== null) {
        clearTimeout(slugDebounceReference.current)
      }

      if (candidate.trim() === '') {
        setState(previous => ({ ...previous, slugCheckStatus: 'idle' }))
        return
      }

      setState(previous => ({ ...previous, slugCheckStatus: 'checking' }))

      slugDebounceReference.current = setTimeout(() => {
        void checkShareSlugAvailability({
          slug: candidate,
          quickPassUserId: quickPassUser.id,
        }).then(result => {
          if (result === null) {
            setState(previous => ({ ...previous, slugCheckStatus: 'idle' }))
            return
          }
          const nextStatus: SlugCheckStatus =
            result.isValid === false
              ? 'invalid'
              : result.isAvailable === true
                ? 'ok'
                : 'taken'
          setState(previous => ({
            ...previous,
            slugCheckStatus: nextStatus,
            slugCheckResult: result,
          }))
        })
      }, SLUG_DEBOUNCE_MS)
    },
    [quickPassUser],
  )

  const handleSlugChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const next = event.target.value.toLowerCase().replace(/[^a-z0-9-]/gu, '')
      setState(previous => ({
        ...previous,
        slugDraft: next,
        hasPendingChanges: true,
        successMessage: '',
      }))
      runSlugCheck(next)
    },
    [runSlugCheck],
  )

  const handleToggleVisibility = useCallback(
    (key: keyof QuickPassSharePageVisibility): void => {
      setState(previous => ({
        ...previous,
        visibilityDraft: {
          ...previous.visibilityDraft,
          [key]: previous.visibilityDraft[key] === false,
        },
        hasPendingChanges: true,
        successMessage: '',
      }))
    },
    [],
  )

  const handleSave = useCallback(async (): Promise<void> => {
    if (quickPassUser === null) return

    setState(previous => ({
      ...previous,
      isSaving: true,
      errorMessage: '',
      successMessage: '',
    }))

    const result = await saveQuickPassSharePage({
      quickPassUserId: quickPassUser.id,
      slug: state.slugDraft,
      headline: state.headlineDraft,
      customMessage: state.customMessageDraft,
      visibility: state.visibilityDraft,
      isActive: state.isActiveDraft,
    })

    if (result === null) {
      setState(previous => ({
        ...previous,
        isSaving: false,
        errorMessage:
          'Couldn’t save — that username might be taken or invalid. Try another.',
      }))
      return
    }

    setState(previous => ({
      ...previous,
      page: result.page,
      slugDraft: result.page.slug,
      visibilityDraft: {
        ...defaultQuickPassSharePageVisibility,
        ...result.page.visibility,
      },
      isActiveDraft: result.page.isActive,
      isSaving: false,
      hasPendingChanges: false,
      successMessage:
        result.status === 'created' ? 'Your QuickPass page is live!' : 'Saved.',
    }))
  }, [
    quickPassUser,
    state.slugDraft,
    state.headlineDraft,
    state.customMessageDraft,
    state.visibilityDraft,
    state.isActiveDraft,
  ])

  const handleRegenerate = useCallback(async (): Promise<void> => {
    if (quickPassUser === null) return
    setState(previous => ({ ...previous, isRegenerating: true }))
    const page = await regenerateQuickPassShareToken(quickPassUser.id)
    setState(previous => ({
      ...previous,
      page: page ?? previous.page,
      isRegenerating: false,
      successMessage:
        page !== null
          ? 'New link generated — the old one no longer works.'
          : previous.successMessage,
    }))
  }, [quickPassUser])

  const handleCopyLink = useCallback((): void => {
    if (state.page === null) return
    const link = buildShareLink(state.page)
    void navigator.clipboard
      .writeText(link)
      .then(() => {
        setState(previous => ({ ...previous, successMessage: 'Link copied!' }))
      })
      .catch(() => {
        setState(previous => ({
          ...previous,
          errorMessage: 'Couldn’t copy — select the link and copy manually.',
        }))
      })
  }, [state.page])

  const handleToggleActive = useCallback((): void => {
    setState(previous => ({
      ...previous,
      isActiveDraft: previous.isActiveDraft === false,
      hasPendingChanges: true,
      successMessage: '',
    }))
  }, [])

  if (quickPassUser === null) {
    return null
  }

  if (state.isLoading === true) {
    return (
      <ManagerRoot>
        <Lede>Loading your share settings…</Lede>
      </ManagerRoot>
    )
  }

  const slugHintText: Record<SlugCheckStatus, string> = {
    idle: '4–32 letters, numbers, or hyphens.',
    checking: 'Checking availability…',
    ok: 'Available',
    taken: 'Already taken — try another',
    invalid: 'Invalid — use 4–32 letters, numbers, or hyphens',
  }

  const slugHintIcon: Partial<Record<SlugCheckStatus, IconDefinition>> = {
    ok: faCheck,
    taken: faXmark,
    invalid: faXmark,
  }

  const currentSlugHintIcon = slugHintIcon[state.slugCheckStatus]

  const canSave =
    state.slugDraft.trim() !== '' &&
    state.slugCheckStatus !== 'taken' &&
    state.slugCheckStatus !== 'invalid' &&
    state.slugCheckStatus !== 'checking' &&
    state.isSaving === false

  return (
    <ManagerRoot>
      <TopRow>
        <Lede>
          Send landlords a polished page about you — what you’re looking for,
          how to reach you, and what’s verified. You control exactly what shows.
        </Lede>
      </TopRow>

      <SlugRow>
        <FieldLabel htmlFor="share-slug">Your QuickPass username</FieldLabel>
        <SlugInputRow>
          <SlugPrefix>app.quickpass.club/me/</SlugPrefix>
          <SlugInput
            id="share-slug"
            value={state.slugDraft}
            onChange={handleSlugChange}
            placeholder="your-name"
            autoComplete="off"
            spellCheck={false}
          />
        </SlugInputRow>
        <SlugHint tone={state.slugCheckStatus}>
          {currentSlugHintIcon !== undefined ? (
            <Icon icon={currentSlugHintIcon} />
          ) : null}{' '}
          {slugHintText[state.slugCheckStatus]}
        </SlugHint>
      </SlugRow>

      <FieldStack>
        <FieldLabel htmlFor="share-headline">Headline</FieldLabel>
        <TextField
          id="share-headline"
          value={state.headlineDraft}
          onChange={event =>
            setState(previous => ({
              ...previous,
              headlineDraft: event.target.value.slice(0, 120),
              hasPendingChanges: true,
              successMessage: '',
            }))
          }
          placeholder="e.g. Quiet professional looking for a 2-bed in the west end"
        />
      </FieldStack>

      <FieldStack>
        <FieldLabel htmlFor="share-message">A note to landlords</FieldLabel>
        <TextArea
          id="share-message"
          value={state.customMessageDraft}
          onChange={event =>
            setState(previous => ({
              ...previous,
              customMessageDraft: event.target.value.slice(0, 600),
              hasPendingChanges: true,
              successMessage: '',
            }))
          }
          placeholder="Anything you'd want a landlord to know — pets, work situation, references available on request…"
        />
      </FieldStack>

      <FieldStack>
        <FieldLabel>What landlords can see</FieldLabel>
        <VisibilityList>
          {VISIBILITY_LABELS.map(item => {
            const isOn = state.visibilityDraft[item.key] === true
            return (
              <VisibilityRow key={item.key}>
                <VisibilityText>
                  <VisibilityName>{item.label}</VisibilityName>
                  <VisibilityDescription>
                    {item.description}
                  </VisibilityDescription>
                </VisibilityText>
                <ToggleSwitch
                  isOn={isOn}
                  label={`Toggle ${item.label}`}
                  onToggle={() => handleToggleVisibility(item.key)}
                />
              </VisibilityRow>
            )
          })}
        </VisibilityList>
      </FieldStack>

      {state.errorMessage !== '' ? (
        <Banner tone="error">{state.errorMessage}</Banner>
      ) : null}
      {state.successMessage !== '' ? (
        <Banner tone="success">{state.successMessage}</Banner>
      ) : null}

      <ShareLinkActions>
        <PrimaryButton
          type="button"
          onClick={() => {
            void handleSave()
          }}
          disabled={canSave === false}
        >
          {state.isSaving === true
            ? 'Saving…'
            : state.page === null
              ? 'Create my page'
              : 'Save changes'}
        </PrimaryButton>
      </ShareLinkActions>

      {state.page !== null ? (
        <ShareLinkRow>
          <FieldLabel>Your share link</FieldLabel>
          {state.isActiveDraft === false ? (
            <InactiveBanner>
              Your pass is hidden — landlords with the link will see a “hidden”
              message until you turn it back on.
            </InactiveBanner>
          ) : null}
          <ShareLinkBox>{buildShareLink(state.page)}</ShareLinkBox>
          <ShareLinkActions>
            <PrimaryButton type="button" onClick={handleCopyLink}>
              Copy link
            </PrimaryButton>
            <SecondaryButton type="button" onClick={handleToggleActive}>
              {state.isActiveDraft === true ? 'Hide my pass' : 'Show my pass'}
            </SecondaryButton>
            <DangerLink
              type="button"
              onClick={() => {
                void handleRegenerate()
              }}
              disabled={state.isRegenerating}
            >
              {state.isRegenerating === true
                ? 'Regenerating…'
                : 'Revoke + new link'}
            </DangerLink>
          </ShareLinkActions>
          <StatsRow>
            <span>
              <Icon icon={faEye} /> {state.page.viewCount} view
              {state.page.viewCount === 1 ? '' : 's'}
            </span>
            <span>{formatLastViewed(state.page.lastViewedAt)}</span>
          </StatsRow>
        </ShareLinkRow>
      ) : null}
    </ManagerRoot>
  )
}

export default SharePageManager
